import {css, useTheme} from '@mui/material';
import {RadioProps} from '@mui/material/Radio';
import SvgIcon from '@mui/material/SvgIcon';
import * as React from 'react';
import {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import {useSpring, animated} from 'react-spring';
import badgeCheckIcon from 'src/assets/icons/badgeCheckIcon.svg';
import {GetStylesFunction} from 'src/js/common/types';
import {useAppSelector} from 'src/js/store_hooks';

import {DlMuiTypography} from './DlMuiTypography';
import {getSearchString} from '../common';
import {LOCATION_REDUCER_STATE_ID} from '../containers/location';

interface OwnProps extends RadioProps {
  showSuccessWindow: boolean;
  handleCloseSuccessWindow: () => void;
  modifiedTime?: string;
  displayValue?: string;
  buttonTitle?: string;
}

type Props = OwnProps;

export const DlMuiSuccessCheck: React.FC<Props> = (props) => {
  const {modifiedTime, showSuccessWindow, handleCloseSuccessWindow, displayValue, buttonTitle} = props;

  const theme = useTheme();
  const styles = getStyles({theme});
  const queryParameters = useAppSelector((state) => state[LOCATION_REDUCER_STATE_ID].parameters);

  const [start, setStart] = useState<boolean>(showSuccessWindow);

  const {width} = useSpring({
    width: start ? '100%' : '0%',
    config: {duration: 3000},
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setStart(false);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      handleCloseSuccessWindow();
    }, 3100);

    return () => clearTimeout(timer);
  }, [handleCloseSuccessWindow]);

  // TODO-JK: move to document package and import ROUTE_HOSPITALIZATIONS
  const route = '/hospitalizedPatients';
  const indexPath = '';
  const searchString = getSearchString(indexPath, route, queryParameters);

  return (
    <div css={styles.wrapper}>
      <animated.div style={{width}} css={styles.progressBar} />
      <div css={styles.content}>
        <SvgIcon css={styles.badgeCheckIcon} component={badgeCheckIcon} inheritViewBox />
        <DlMuiTypography variant={'basicTextBold'} value={displayValue} />
        <DlMuiTypography variant={'value'} value={modifiedTime} />
        <Link css={styles.link} onClick={handleCloseSuccessWindow} to={`${route}?${searchString}`}>
          {buttonTitle}
        </Link>
      </div>
    </div>
  );
};

DlMuiSuccessCheck.displayName = 'DlMuiSuccessCheck';

const getStyles: GetStylesFunction = (options) => ({
  wrapper: css`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${options?.theme?.spacing(1)};
    width: 100%;
    border-radius: ${options?.theme?.spacing(4)};
    background-color: ${options?.theme?.palette.custom.green['50']};
    height: 124px;
    overflow: hidden;
    flex: 2;
  `,
  content: css`
    padding: ${options?.theme?.spacing(1.5, 4, 1.5, 4)};
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${options?.theme?.spacing(2)};
    text-align: center;
  `,
  progressBar: css`
    position: absolute;
    top: 0;
    width: 100%;
    height: 3px;
    background-color: ${options?.theme?.palette.custom.green['200']};
  `,
  badgeCheckIcon: css`
    width: 24px;
    height: 24px;
    color: #24a148;
  `,
  link: css`
    color: var(--dlTheme-500);
    text-decoration: underline;
    cursor: pointer;

    &:hover,
    &:focus {
      color: var(--dlTheme-600);
    }

    &:active {
      color: var(--dlTheme-700);
    }
  `,
});
